import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const SEO = ({ 
  titleFr, 
  titleEn, 
  descriptionFr, 
  descriptionEn,
  keywords = '',
  noindex = false 
}) => {
  const { language, initialized } = useLanguage();
  const location = useLocation();
  
  const baseUrl = 'https://mtl.taxi';
  const currentPath = location.pathname;
  const currentUrl = `${baseUrl}${currentPath}`;
  
  // Route mappings for alternate URLs
  const routeMappings = {
    '/fr': '/en',
    '/fr/aeroport-yul': '/en/yul-airport',
    '/fr/services': '/en/services',
    '/fr/tarifs': '/en/fares',
    '/fr/zones-desservies': '/en/service-areas',
    '/fr/faq': '/en/faq',
    '/fr/contact': '/en/contact',
    '/fr/confidentialite': '/en/privacy-policy',
    '/fr/conditions-utilisation': '/en/terms-of-use',
    '/fr/avertissement': '/en/disclaimer',
    '/en': '/fr',
    '/en/yul-airport': '/fr/aeroport-yul',
    '/en/services': '/fr/services',
    '/en/fares': '/fr/tarifs',
    '/en/service-areas': '/fr/zones-desservies',
    '/en/faq': '/fr/faq',
    '/en/contact': '/fr/contact',
    '/en/privacy-policy': '/fr/confidentialite',
    '/en/terms-of-use': '/fr/conditions-utilisation',
    '/en/disclaimer': '/fr/avertissement',
  };
  
  const alternatePath = routeMappings[currentPath] || (language === 'fr' ? '/en' : '/fr');
  const alternateUrl = `${baseUrl}${alternatePath}`;
  const frUrl = currentPath.startsWith('/fr') ? currentUrl : alternateUrl;
  const enUrl = currentPath.startsWith('/en') ? currentUrl : alternateUrl;
  
  const title = language === 'fr' ? (titleFr || 'MTL Taxi') : (titleEn || 'MTL Taxi');
  const description = language === 'fr' ? (descriptionFr || '') : (descriptionEn || '');
  
  const fullTitle = title ? `${title} | MTL Taxi` : 'MTL Taxi';

  // Update document meta tags dynamically
  React.useEffect(() => {
    if (!initialized) return;
    
    // Title
    document.title = fullTitle;
    document.documentElement.lang = language;
    
    // Update or create meta tags
    const updateMeta = (selector, content, attr = 'content') => {
      let el = document.querySelector(selector);
      if (el) {
        el.setAttribute(attr, content);
      }
    };
    
    const updateOrCreateMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (el) {
        el.setAttribute('content', content);
      } else {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        el.setAttribute('content', content);
        document.head.appendChild(el);
      }
    };
    
    const updateOrCreateLink = (rel, href, hreflang = null) => {
      let selector = `link[rel="${rel}"]`;
      if (hreflang) {
        selector = `link[rel="${rel}"][hreflang="${hreflang}"]`;
      }
      let el = document.querySelector(selector);
      if (el) {
        el.setAttribute('href', href);
      } else {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        el.setAttribute('href', href);
        if (hreflang) {
          el.setAttribute('hreflang', hreflang);
        }
        document.head.appendChild(el);
      }
    };
    
    // Primary meta tags
    updateOrCreateMeta('title', fullTitle);
    updateOrCreateMeta('description', description);
    if (keywords) {
      updateOrCreateMeta('keywords', keywords);
    }
    updateOrCreateMeta('language', language === 'fr' ? 'French' : 'English');
    
    // Robots
    if (noindex) {
      updateOrCreateMeta('robots', 'noindex, nofollow');
    } else {
      updateOrCreateMeta('robots', 'index, follow');
    }
    
    // Canonical URL
    updateOrCreateLink('canonical', currentUrl);
    
    // Hreflang tags
    updateOrCreateLink('alternate', frUrl, 'fr');
    updateOrCreateLink('alternate', enUrl, 'en');
    updateOrCreateLink('alternate', frUrl, 'x-default');
    
    // Open Graph
    updateOrCreateMeta('og:title', fullTitle, true);
    updateOrCreateMeta('og:description', description, true);
    updateOrCreateMeta('og:url', currentUrl, true);
    updateOrCreateMeta('og:locale', language === 'fr' ? 'fr_CA' : 'en_CA', true);
    updateOrCreateMeta('og:locale:alternate', language === 'fr' ? 'en_CA' : 'fr_CA', true);
    
    // Twitter
    updateOrCreateMeta('twitter:title', fullTitle);
    updateOrCreateMeta('twitter:description', description);
    updateOrCreateMeta('twitter:url', currentUrl);
    
  }, [fullTitle, language, description, keywords, currentUrl, alternateUrl, frUrl, enUrl, initialized, noindex]);

  return null;
};

// Pre-defined SEO configs for each page
export const pageSEO = {
  home: {
    titleFr: 'Service de taxi fiable à Montréal | 438-600-4440',
    titleEn: 'Reliable Taxi Service in Montreal | 438-600-4440',
    descriptionFr: 'MTL Taxi - Service de taxi 24/7 dans le Grand Montréal. Navette aéroport YUL, minivan 6 passagers, taxi accessible. Réservez en ligne ou appelez 438-600-4440.',
    descriptionEn: 'MTL Taxi - 24/7 taxi service in Greater Montreal. YUL airport shuttle, 6-passenger minivan, wheelchair accessible. Book online or call 438-600-4440.',
    keywords: 'taxi montreal, taxi aeroport yul, taxi 24/7, taxi minivan, taxi accessible, MTL Taxi',
  },
  airport: {
    titleFr: 'Taxi Aéroport Montréal-Trudeau (YUL) | Tarifs fixes',
    titleEn: 'Montreal-Trudeau Airport (YUL) Taxi | Fixed Rates',
    descriptionFr: 'Navette taxi aéroport YUL Montréal-Trudeau. Tarifs fixes par zone, service 24/7, suivi des vols. MTL Taxi 438-600-4440.',
    descriptionEn: 'YUL Montreal-Trudeau Airport taxi shuttle. Fixed rates by zone, 24/7 service, flight tracking. MTL Taxi 438-600-4440.',
    keywords: 'taxi aeroport yul, taxi montreal trudeau, navette aeroport montreal, airport taxi, YUL taxi',
  },
  services: {
    titleFr: 'Services de Taxi | Minivan, Accessible, Corporatif',
    titleEn: 'Taxi Services | Minivan, Accessible, Corporate',
    descriptionFr: 'Services MTL Taxi: berline, minivan 6 passagers, taxi accessible fauteuil roulant, comptes corporatifs, survoltage, déverrouillage. 438-600-4440.',
    descriptionEn: 'MTL Taxi services: sedan, 6-passenger minivan, wheelchair accessible, corporate accounts, battery boost, door unlock. 438-600-4440.',
    keywords: 'taxi minivan montreal, taxi accessible, taxi corporatif, wheelchair taxi montreal',
  },
  fares: {
    titleFr: 'Tarifs de Taxi Montréal | Prix réglementés CTQ',
    titleEn: 'Montreal Taxi Fares | CTQ Regulated Rates',
    descriptionFr: 'Tarifs taxi réglementés Commission des transports du Québec. Prix de départ, tarif au kilomètre, tarif de nuit. MTL Taxi 438-600-4440.',
    descriptionEn: 'Quebec regulated taxi fares. Starting fare, per kilometer rate, night rate. MTL Taxi Montreal 438-600-4440.',
    keywords: 'tarif taxi montreal, prix taxi, taxi fare montreal, cout taxi',
  },
  areas: {
    titleFr: 'Zones Desservies | Montréal, Laval, Longueuil',
    titleEn: 'Service Areas | Montreal, Laval, Longueuil',
    descriptionFr: 'MTL Taxi dessert Grand Montréal: Centre-ville, Saint-Laurent, Laval, Longueuil, Ouest-de-l\'Île, Rive-Sud. Service 24/7. 438-600-4440.',
    descriptionEn: 'MTL Taxi serves Greater Montreal: Downtown, Saint-Laurent, Laval, Longueuil, West Island, South Shore. 24/7 service. 438-600-4440.',
    keywords: 'taxi saint-laurent, taxi laval, taxi longueuil, taxi rive-sud, taxi west island',
  },
  faq: {
    titleFr: 'Questions Fréquentes | FAQ Taxi Montréal',
    titleEn: 'Frequently Asked Questions | Montreal Taxi FAQ',
    descriptionFr: 'FAQ MTL Taxi: réservation, paiement, animaux, bagages, tarifs aéroport YUL, taxi accessible. Appelez 438-600-4440.',
    descriptionEn: 'MTL Taxi FAQ: booking, payment, pets, luggage, YUL airport rates, accessible taxi. Call 438-600-4440.',
    keywords: 'faq taxi montreal, questions taxi',
  },
  contact: {
    titleFr: 'Contactez-Nous | Réservation Taxi 438-600-4440',
    titleEn: 'Contact Us | Taxi Booking 438-600-4440',
    descriptionFr: 'Contactez MTL Taxi 438-600-4440. Disponible 24/7. Réservez en ligne. Service Grand Montréal, aéroport YUL.',
    descriptionEn: 'Contact MTL Taxi 438-600-4440. Available 24/7. Book online. Greater Montreal service, YUL airport.',
    keywords: 'contact taxi montreal, reservation taxi, taxi telephone',
  },
  privacy: {
    titleFr: 'Politique de Confidentialité',
    titleEn: 'Privacy Policy',
    descriptionFr: 'Politique de confidentialité MTL Taxi. Protection de vos informations personnelles.',
    descriptionEn: 'MTL Taxi privacy policy. Protection of your personal information.',
    keywords: '',
  },
  terms: {
    titleFr: 'Conditions d\'Utilisation',
    titleEn: 'Terms of Use',
    descriptionFr: 'Conditions d\'utilisation des services MTL Taxi. Réservations, tarifs, paiement.',
    descriptionEn: 'MTL Taxi terms of use. Reservations, fares, payment.',
    keywords: '',
  },
  disclaimer: {
    titleFr: 'Avertissement Légal',
    titleEn: 'Legal Disclaimer',
    descriptionFr: 'Avertissement légal MTL Taxi. Exactitude des informations et tarifs.',
    descriptionEn: 'MTL Taxi legal disclaimer. Accuracy of information and fares.',
    keywords: '',
  },
};

export default SEO;
